import React, { lazy, useEffect } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

import App from "./App.jsx";

import PrivateRoute from "./components/PrivateRoute.jsx";
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import RoleProtectedRoute from "./components/RoleProtectedRoute.jsx";
import { useAuth } from "./context/AuthContext.jsx";

import { loadLanguage } from "./i18nLoader.js";

/** ───────────── Public pages (lazy) ───────────── **/
const PublicSearch = lazy(() => import("./pages/public/PublicSearch.jsx"));
const FavoritesPage = lazy(() => import("./pages/public/FavoritesPage.jsx"));
const DevMissingKeysPage = lazy(() => import("./pages/dev/DevMissingKeysPage.jsx"));
const VerifyOwnership = lazy(() => import("./pages/VerifyOwnership.jsx"));
const ClaimBusiness = lazy(() => import("./pages/ClaimBusiness.jsx"));
const AuthForm = lazy(() => import("./components/AuthForm.jsx"));
const PaymentSuccess = lazy(() => import("./pages/public/PaymentSuccess.jsx"));
const PaymentCancel = lazy(() => import("./pages/public/PaymentCancel.jsx"));

const PublicBusinessProfile = lazy(() =>
    import("./components/PublicView/PublicBusinessProfile.jsx")
);

const NotFound = lazy(() => import("./pages/public/NotFound.jsx"));
const Payment = lazy(() => import("./pages/Payment.jsx"));
const BillingInvoices = lazy(() => import("./pages/BillingInvoices.jsx"));

/** ───────────── Dashboard (lazy) ───────────── **/
const BusinessDashboard = lazy(() =>
    import("./components/BusinessView/BusinessDashboard.jsx")
);

/** ───────────── Account / profile (lazy) ───────────── **/
const AccountSettings = lazy(() => import("./pages/AccountSettings.jsx"));

/** ───────────── Admin (lazy) ───────────── **/
const AdminRoute = lazy(() => import("./pages/admin/AdminRoute.jsx"));
const AdminPanel = lazy(() => import("./pages/admin/AdminPanel.jsx"));
const AdminStats = lazy(() => import("./pages/admin/AdminStats.jsx"));
const VerificationLogs = lazy(() => import("./pages/admin/VerificationLogs.jsx"));
const Settings = lazy(() => import("./pages/admin/Settings.jsx"));
const RolesManager = lazy(() => import("./pages/admin/RolesManager.jsx"));
const AdminSearchLogs = lazy(() => import("./pages/admin/AdminSearchLogs.jsx"));
const AuditLogViewer = lazy(() => import("./pages/admin/AuditLogViewer.jsx"));
const BusinessInsights = lazy(() =>
    import("./pages/admin/BusinessInsights.jsx")
);
const BusinessApprovalQueue = lazy(() =>
    import("./pages/admin/BusinessApprovalQueue.jsx")
);
const PermissionPreview = lazy(() =>
    import("./pages/admin/PermissionPreview.jsx")
);
const AdminDataExport = lazy(() =>
    import("./pages/admin/AdminDataExport.jsx")
);
const SystemCheckToolLazy = lazy(() => import("./components/SystemCheckTool.jsx"));

function LangRedirect() {
    const { code } = useParams();

    useEffect(() => {
        if (code) loadLanguage(code);
    }, [code]);

    return <Navigate to="/search" replace />;
}

function HomeLanding() {
    const { user, currentUser, isAdmin, isOwner } = useAuth();
    const activeUser = user || currentUser;

    if (!activeUser) {
        return <Navigate to="/auth" replace />;
    }

    if (isOwner || isAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    return <Navigate to="/search" replace />;
}

function LegacyOwnerRedirect() {
    const location = useLocation();
    return <Navigate to={`/dashboard${location.search || ""}`} replace />;
}

function LegacyOwnerIdRedirect() {
    const { id } = useParams();
    const location = useLocation();
    const search = location.search || "";
    return <Navigate to={`/dashboard/${id}${search}`} replace />;
}

function RouteErr() {
    return (
        <div
            style={{
                maxWidth: 720,
                margin: "4rem auto",
                padding: 24,
                background: "#fff",
                borderRadius: 16,
                border: "1px solid #e2e8f0",
            }}
        >
            <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
                Something went wrong
            </h2>
            <p style={{ color: "#475569" }}>
                This route failed to load. Try refreshing or going back.
            </p>
            <a
                href="/auth"
                style={{
                    display: "inline-block",
                    marginTop: 16,
                    padding: "8px 12px",
                    background: "var(--accent)",
                    color: "#fff",
                    borderRadius: 12,
                    textDecoration: "none",
                }}
            >
                Go to Login
            </a>
        </div>
    );
}

export default [
    {
        path: "/",
        element: <App />,
        errorElement: <RouteErr />,
        children: [
            { index: true, element: <HomeLanding /> },

            {
                path: "search",
                element: (
                    <PrivateRoute>
                        <PublicSearch />
                    </PrivateRoute>
                ),
            },
            {
                path: "favorites",
                element: (
                    <PrivateRoute>
                        <FavoritesPage />
                    </PrivateRoute>
                ),
            },
            {
                path: "directory",
                element: (
                    <PrivateRoute>
                        <PublicSearch />
                    </PrivateRoute>
                ),
            },
            {
                path: "business-search",
                element: (
                    <PrivateRoute>
                        <PublicSearch />
                    </PrivateRoute>
                ),
            },

            {
                path: "lang/:code",
                element: (
                    <PrivateRoute>
                        <LangRedirect />
                    </PrivateRoute>
                ),
            },

            { path: "auth", element: <AuthForm /> },
            { path: "login", element: <Navigate to="/auth" replace /> },
            { path: "register", element: <Navigate to="/auth" replace /> },

            {
                path: "account",
                element: (
                    <ProtectedRoute>
                        <AccountSettings />
                    </ProtectedRoute>
                ),
            },

            {
                path: "payment",
                element: (
                    <PrivateRoute>
                        <Payment />
                    </PrivateRoute>
                ),
            },
            {
                path: "billing/invoices",
                element: (
                    <PrivateRoute>
                        <BillingInvoices />
                    </PrivateRoute>
                ),
            },

            { path: "payment-success", element: <PaymentSuccess /> },
            { path: "payment/success", element: <PaymentSuccess /> },

            { path: "payment-cancel", element: <PaymentCancel /> },
            { path: "payment/cancel", element: <PaymentCancel /> },

            {
                path: "dev/missing-keys",
                element: (
                    <PrivateRoute>
                        <DevMissingKeysPage />
                    </PrivateRoute>
                ),
            },
            {
                path: "system-check",
                element: (
                    <PrivateRoute>
                        <SystemCheckToolLazy />
                    </PrivateRoute>
                ),
            },

            {
                path: "business/:businessId",
                element: (
                    <PrivateRoute>
                        <PublicBusinessProfile />
                    </PrivateRoute>
                ),
            },

            {
                path: "claim/:businessId",
                element: (
                    <PrivateRoute>
                        <ClaimBusiness />
                    </PrivateRoute>
                ),
            },
            {
                path: "verify/:token",
                element: (
                    <PrivateRoute>
                        <VerifyOwnership />
                    </PrivateRoute>
                ),
            },

            {
                path: "dashboard",
                element: (
                    <RoleProtectedRoute requiredRoles={["owner", "admin"]}>
                        <BusinessDashboard />
                    </RoleProtectedRoute>
                ),
            },
            {
                path: "dashboard/:id",
                element: (
                    <RoleProtectedRoute requiredRoles={["owner", "admin"]}>
                        <BusinessDashboard />
                    </RoleProtectedRoute>
                ),
            },

            {
                path: "businesses",
                element: (
                    <PrivateRoute>
                        <LegacyOwnerRedirect />
                    </PrivateRoute>
                ),
            },
            {
                path: "businesses/:id",
                element: (
                    <PrivateRoute>
                        <LegacyOwnerIdRedirect />
                    </PrivateRoute>
                ),
            },
            {
                path: "my-business",
                element: (
                    <PrivateRoute>
                        <LegacyOwnerRedirect />
                    </PrivateRoute>
                ),
            },
            {
                path: "my-business/:id",
                element: (
                    <PrivateRoute>
                        <LegacyOwnerIdRedirect />
                    </PrivateRoute>
                ),
            },
            {
                path: "business-dashboard",
                element: (
                    <PrivateRoute>
                        <LegacyOwnerRedirect />
                    </PrivateRoute>
                ),
            },
            {
                path: "business-dashboard/:id",
                element: (
                    <PrivateRoute>
                        <LegacyOwnerIdRedirect />
                    </PrivateRoute>
                ),
            },

            {
                path: "admin",
                element: <AdminRoute />,
                errorElement: <RouteErr />,
                children: [
                    { index: true, element: <AdminPanel /> },
                    { path: "stats", element: <AdminStats /> },
                    { path: "verification", element: <VerificationLogs /> },
                    { path: "settings", element: <Settings /> },
                    { path: "roles", element: <RolesManager /> },
                    { path: "search-logs", element: <AdminSearchLogs /> },

                    {
                        path: "business-insights",
                        element: (
                            <RoleProtectedRoute requiredRoles={["admin", "moderator"]}>
                                <BusinessInsights />
                            </RoleProtectedRoute>
                        ),
                    },

                    {
                        path: "business-approvals",
                        element: (
                            <RoleProtectedRoute requiredRoles={["admin", "moderator"]}>
                                <BusinessApprovalQueue />
                            </RoleProtectedRoute>
                        ),
                    },

                    {
                        path: "audit-logs",
                        element: (
                            <RoleProtectedRoute requiredRoles={["admin", "moderator"]}>
                                <AuditLogViewer />
                            </RoleProtectedRoute>
                        ),
                    },

                    {
                        path: "permission-preview",
                        element: (
                            <RoleProtectedRoute requiredRoles={["admin", "moderator"]}>
                                <PermissionPreview />
                            </RoleProtectedRoute>
                        ),
                    },

                    {
                        path: "data-export",
                        element: (
                            <RoleProtectedRoute requiredRoles={["admin"]}>
                                <AdminDataExport />
                            </RoleProtectedRoute>
                        ),
                    },

                    { path: "*", element: <NotFound /> },
                ],
            },

            { path: "*", element: <Navigate to="/auth" replace /> },
        ],
    },
];